import { useUpdateFacility } from "@features/ax/assetRegistry/facilities/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { toast } from "@shared/ui/use-toast";
import { downloadFileByUrl, viewFileByUrl } from "@shared/utils/utils";
import { Download, Eye, File, FileText, Image, Upload } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FileUploadDialog from "../../../../../../shared/DocumentUploadModal";

interface DocumentsTabProps {
  data: any[];
}

const FileCard: React.FC<{ file: any }> = ({ file }) => {
  const handleDownload = () => {
    downloadFileByUrl(file.fileUrl);
  };

  const handleView = () => {
    viewFileByUrl(file.fileUrl);
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <HoverRevealText text={file.fileName}></HoverRevealText>
            <div className="space-y-1 text-sm text-gray-600">
              <p>Date: {file.createdDate ?? "NA"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="h-8 w-8 p-0"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleView}
              className="h-8 w-8 p-0"
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const FilesLanding: React.FC<DocumentsTabProps> = ({ data }) => {
  const { id } = useParams();
  const { mutateAsync: updateFacility } = useUpdateFacility();

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  useEffect(() => {
    logEvent("files_tab_viewed");
  }, []);
  const handleFilesUpload = async (files: File[]) => {
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("documents", file);
      });

      await updateFacility({ facilityId: id, formData });
    } catch (err: any) {
      toast({
        title: "Upload failed",
        description: err?.message || "Something went wrong while uploading",
        variant: "destructive",
      });
    }
  };

  const mediaExtensions = ["jpg", "jpeg", "png", "mp4"];
  const documentExtensions = ["pdf", "doc", "docx", "xls", "xlsx"];

  const mediaFiles = data?.filter((f) =>
    mediaExtensions.includes(f.fileName?.split(".").pop()?.toLowerCase())
  );

  const documentFiles = data?.filter((f) =>
    documentExtensions.includes(f.fileName?.split(".").pop()?.toLowerCase())
  );

  const otherFiles = data?.filter(
    (f) =>
      !documentExtensions.includes(
        f.fileName?.split(".").pop()?.toLowerCase()
      ) &&
      !mediaExtensions.includes(f.fileName?.split(".").pop()?.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <FileText className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <PageHeader
              title="Files & Documents"
              subtitle="Manage facility-related documents, media files, and attachments"
            />
          </div>
        </div>
        <Button
          className="flex items-center gap-2"
          onClick={() => setIsUploadOpen(true)}
        >
          <Upload className="h-4 w-4" />
          Upload Files
        </Button>
      </div>

      <FileUploadDialog
        isOpen={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        onFilesUpload={handleFilesUpload}
        title="Upload Asset Documents"
        description="Attach certificates, images, and other files related to this asset"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.xls,.xlsx"
      />

      {/* Media Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Media Files ({mediaFiles?.length})
        </h3>
        {mediaFiles?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mediaFiles?.map((file) => (
              <FileCard key={file.id} file={file} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Image className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No media files uploaded yet</p>
          </div>
        )}
      </div>

      {/* Documents Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Documents ({documentFiles?.length})
        </h3>
        {documentFiles?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documentFiles?.map((file) => (
              <FileCard key={file.id} file={file} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <File className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No documents uploaded yet</p>
          </div>
        )}
      </div>
      {otherFiles?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Others ({otherFiles.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherFiles.map((file) => (
              <FileCard key={file.id} file={file} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
